/**
 * Umbrales de la calibración del motor: su forma y sus valores por DEFAULT, en UN solo lugar.
 *
 * Los usan el worker (lo que el motor aplica), la API (lo que devuelve el GET de calibración y los
 * defaults del PUT) y el panel staff (lo que muestra). Antes el panel rellenaba con números escritos a
 * mano (`?? 0.5`) mientras el worker usaba 0,6: el panel mostraba un valor que el motor no aplicaba.
 * Lo guardado se combina con estos defaults con `umbralesEfectivos`, la misma función en los tres.
 * Movido tal cual desde apps/worker/src/jobs/apuCalibracion.ts (mismos valores, mismas notas).
 */
import { PARAMETROS_MEDICION_IFC_DEFAULT, type ParametrosMedicionIfc } from './parametrosMedicionIfc.js';
import { PARAMETROS_MEDICION_DWG_DEFAULT, type ParametrosMedicionDwg } from './parametrosMedicionDwg.js';
import { PARAMETROS_CHEQUEO_PLANO_DEFAULT, type ParametrosChequeoPlano } from './chequeoDelPlano.js';
import { PARAMETROS_CAMBIO_DE_PLANO_DEFAULT, type ParametrosCambioDePlano } from './cambioDePlano.js';

/**
 * Umbrales del motor (calibración `motor_calibracion.calibracion.umbrales`). Los `ifc_*` (medición de
 * modelos IFC en el visor de OPUS ci) los usa el NAVEGADOR; los `dwg_*` (medición de planos DWG/DXF)
 * los usa el worker, que se los pasa al lector `plano2svg.py`; el resto, el worker.
 */
export interface UmbralesCalibracion extends ParametrosMedicionIfc, ParametrosMedicionDwg, ParametrosChequeoPlano, ParametrosCambioDePlano {
  /** Match verde (precio directo) si similitud ≥. */
  match_verde_min: number;
  /** Match rojo (manda a APU sintético) si similitud <. */
  match_rojo_max: number;
  /** Filtro de correspondencia real (Fase A 2026-07-08): si NINGÚN candidato de
   * zona gris alcanza esta inclusión de vocabulario discriminativo (IDF del
   * propio catálogo), el concepto va a sin_candidato SIN gastar IA. 0 = apagado.
   * Medido sobre 539 pares reales: 0.15 → 0% de matches validados-SI dañados. */
  match_gate_min: number;
  /** Planos: vínculo especificación de detalle → concepto. Confianza ALTA si el parecido ≥ esto… */
  spec_vinculo_alta_min: number;
  /** …y le gana al 2º candidato por al menos este margen. */
  spec_vinculo_alta_margen: number;
  /** Confianza MEDIA si el parecido ≥ esto; por debajo no se vincula. */
  spec_vinculo_media_min: number;
  /**
   * Cuando la nota nombra el elemento con OTRA palabra («cadena» por «dala»): parecido mínimo del
   * significado (bge-m3, coseno de la nota completa contra el concepto completo) para tenerlos por el
   * mismo elemento. Además tienen que ser la mejor pareja el uno del otro. 1 = apagado.
   */
  spec_vinculo_mismo_elemento_min: number;
  /**
   * «Proponer más» (adicionales e instalaciones de /planos): una propuesta nueva que nombra el MISMO
   * elemento que una de la lista («Refuerzo vertical (castillos)» ↔ «Castillos de concreto armado») se
   * descarta si su significado (bge-m3, coseno de los nombres) es al menos esto. 1 = no se descarta nada.
   */
  necesidad_repetida_min: number;
  /**
   * Evidencia de los jueces de «lo ya incluido» y «el detalle es de otro elemento»: la IA debe copiar una frase LITERAL del texto
   * y el código la verifica. Largo mínimo (en caracteres) de esa cita para que cuente: evita que una palabra suelta valga como prueba.
   */
  cita_min_caracteres: number;
  /** Para que la cita «hable del trabajo» comparte con él al menos una palabra de este largo mínimo (en letras). */
  cita_min_letras_palabra: number;
  /** APU sintético VERDE si la cobertura de catálogo (insumos resueltos) ≥. */
  apu_cobertura_verde_min: number;
  /** APU sintético ROJO si la cobertura de catálogo <. Entre ambos = amarillo. */
  apu_cobertura_roja_max: number;
  /** APU ROJO si los factores reducen el rendimiento ≥ (fracción 0–1, ej. 0.40). */
  apu_reduccion_roja_min: number;
  /** APU AMARILLO si la reducción de rendimiento ≥ (fracción 0–1, ej. 0.20). */
  apu_reduccion_amarilla_min: number;
  /** Caché de precios de mercado (AI-8): un precio consultado hace menos de N
   * días se reusa sin volver a llamar a Serper. Entero de días; 0 = desactivado.
   * (No es un umbral 0–1: es config operativa del caché, editable por Ecosoft.) */
  market_price_cache_ttl_days: number;
  /** Tipo de cambio (migración 074): cuántas HORAS se reusa un cambio ya
   * consultado antes de volver a pedirlo. Entero de horas; 0 = siempre consultar.
   * (No es un umbral 0–1: es config operativa del caché, editable por Ecosoft.) */
  fx_rate_ttl_hours: number;
  /** Cordura de precios: cuántas veces por encima del insumo MÁS CARO de su tipo
   * y unidad en el propio catálogo se tolera un precio antes de descartarlo.
   * La referencia es el catálogo del cliente, no una tabla fija. 0 = apagado.
   * (No es un umbral 0–1: es un multiplicador, editable por Ecosoft.) */
  precio_cordura_factor: number;
  /** Piso de cordura: cuántas veces por DEBAJO del insumo MÁS BARATO de su tipo y
   * unidad en el propio catálogo se tolera un precio antes de marcarlo. 1 = el piso
   * es exactamente el insumo más barato del catálogo; 2 = la mitad de ese.
   * 0 = apagado. Un precio bajo el piso NUNCA queda en cero: se vuelve a resolver y,
   * si no mejora, se conserva marcado (ver garantizarPisoDePrecios).
   * (No es un umbral 0–1: es un divisor, editable por Ecosoft.) */
  precio_piso_factor: number;
  /**
   * Reconciliación de la lectura del catálogo (migr 081/082): cuántos renglones de
   * diferencia se le toleran a UNA página antes de mandarla a releer.
   *
   * 0 = estricto (cualquier diferencia manda a releer), que es el default mientras
   * dirección no decida otra cosa. Medido sobre los cinco documentos de prueba: con 0
   * se releen 0 de 7 páginas en Cañada del Lobo, 0 de 16 en Sierra Grande, 10 de 14 en
   * ANEXO1, 21 de 70 en Docencia III y 70 de 95 en el catálogo de 95 páginas. Subirlo a
   * 1 ahorraría 4, 11 y 2 relecturas respectivamente — y taparía renglones que faltan
   * de verdad, así que no se sube sin decidirlo.
   * (No es un umbral 0–1: es una cantidad de renglones, editable por Ecosoft.)
   */
  reconciliacion_tolerancia_renglones: number;
  /**
   * Tope de páginas a releer por documento. 0 = sin tope. El informe SIEMPRE dice
   * cuántas de cuántas se releyeron, así que un tope no esconde nada.
   * (No es un umbral 0–1: es una cantidad de páginas, editable por Ecosoft.)
   */
  reconciliacion_max_relecturas: number;
  /**
   * ¿La cotización se NIEGA a entregarse si después de releer sigue faltando un renglón?
   * 0 = se entrega y el renglón entra a la hoja MARCADO (default desde 2026-08-13,
   * migr 083). 1 = la cotización se detiene.
   *
   * Por qué el default cambió. La política de "cubrir el 100 %" seguía siendo correcta
   * —a una oferta le falta un renglón y queda afuera— pero tumbar la cotización la
   * cumplía al revés: dejaba al usuario sin hoja Y sin los renglones que el sistema ya
   * tenía detectados, y sin nada que revisar. Entregar con el renglón adentro y marcado
   * cubre el 100 % de verdad: está en la hoja, se ve, y se corrige.
   *
   * Se queda en 1 para el cliente que prefiera no recibir una hoja con renglones a
   * medias: es una decisión de negocio, no un detalle técnico, y se cambia desde el
   * panel sin desplegar nada.
   * (No es un umbral 0–1: es un interruptor, editable por Ecosoft.)
   */
  reconciliacion_exigir_cierre: number;
}


// Defaults = valores EFECTIVOS actuales (no los de la spec aún), para que el
// seed no cambie comportamiento. El ingeniero/panel los afina (D13).
export const UMBRALES_CALIBRACION_DEFAULT: Readonly<UmbralesCalibracion> = {
  match_verde_min: 0.9,
  match_rojo_max: 0.25,
  match_gate_min: 0.15,
  // Vínculo spec→concepto: los mismos valores que `UMBRALES_VINCULO_DEFAULT` de @supernova/quotes
  // (un test verifica que coincidan).
  spec_vinculo_alta_min: 0.5,
  spec_vinculo_alta_margen: 0.1,
  spec_vinculo_media_min: 0.34,
  // Medido 2026-10-01 (bge-m3, 6 sinónimos de obra contra 11 conceptos): los aciertos quedan entre 0.72 y
  // 0.79 y los errores llegan a 0.73 — el umbral solo descarta lo lejano; lo que decide es la pareja mutua.
  spec_vinculo_mismo_elemento_min: 0.7,
  // Medido 2026-10-02 (bge-m3, 28 propuestas contra listas reales de adicionales e instalaciones): con el
  // elemento en común y sin calificativo que lo contradiga, las repetidas dan 0.60–0.80; el umbral solo
  // descarta lo lejano. Ver apps/api/src/services/necesidadesRepetidas.ts.
  necesidad_repetida_min: 0.55,
  cita_min_caracteres: 12,
  cita_min_letras_palabra: 5,
  // Defaults = comportamiento histórico del semáforo (cobertura 100% = verde,
  // >50% fuera de catálogo = rojo; reducción >40% = rojo, >20% = amarillo).
  // Ecosoft los relaja desde el panel.
  apu_cobertura_verde_min: 1.0,
  // Criterio de Sebastián (2026-08-14): "de los sintéticos, que no encuentre el 40% de los
  // recursos, eso sí me preocuparía". O sea: rojo cuando falta 40% o más → cobertura por
  // debajo de 0,6. Antes estaba en 0,5 (rojo recién al faltar más de la mitad), así que un
  // análisis al que le faltaba el 40% salía amarillo. Se mueve el DEFAULT; la calibración ya
  // publicada de cada entorno conserva su valor hasta que se publique una versión nueva
  // desde el panel de Calibración.
  apu_cobertura_roja_max: 0.6,
  apu_reduccion_roja_min: 0.4,
  apu_reduccion_amarilla_min: 0.2,
  // Caché de precios de mercado (AI-8): 21 días por default. Ecosoft lo ajusta
  // desde el panel de Calibración. El merge con la fila guardada + este default
  // hace que las calibraciones existentes tomen 21 sin migración de datos.
  market_price_cache_ttl_days: 21,
  // Tipo de cambio: 24 h. Un cambio del día alcanza para cotizar (las monedas de
  // LatAm no se mueven tanto en una jornada) y evita salir a la red por cada
  // recurso. Ecosoft lo ajusta desde el panel.
  fx_rate_ttl_hours: 24,
  // Cordura de precios: se descarta lo que supere 2× el insumo más caro de su
  // tipo y unidad en el catálogo del cliente. Calibrado contra los dos casos
  // reales que motivaron el control (equipo de mercado y auxiliar de acero,
  // ambos ~2,2× el máximo del catálogo chileno) dejando pasar el insumo más caro
  // legítimo. Ecosoft lo afloja o lo apaga (0) desde el panel.
  precio_cordura_factor: 2,
  // Piso de cordura: 1 = el piso es el insumo MÁS BARATO que el propio catálogo
  // registra para ese tipo y unidad. Medido el 2026-08-12 contra las filas reales
  // de los tres catálogos cargados en local —ECOSTOS México (75.553 recursos en
  // bandas con muestra suficiente), ECOSTOS Chile (76.818) y Colombia (34.250)—:
  // CERO falsos positivos, porque por construcción el piso queda en el precio más
  // bajo que el catálogo admite. Y atrapa el caso real que lo motivó: el andamio a
  // 1,25 la hora, contra un piso de 4,18 (la máquina por hora más barata de ECOSTOS
  // México). Bajarlo a 2 (la mitad del más barato) no atrapa nada más; subirlo al
  // percentil 1 del catálogo atraparía 720 filas legítimas en México y 348 en Chile,
  // así que no se propone. Lo que este piso NO puede atrapar es la grúa a 124 la
  // hora: el catálogo tiene martillos hidráulicos legítimos a 118 y 126 la hora, así
  // que ningún piso derivado de la banda distingue uno del otro — ese caso lo cierran
  // la moneda declarada (migr. 074) y el control de identidad del precio de mercado.
  // Número PROPUESTO: lo confirma Sebastián desde el panel de Calibración.
  precio_piso_factor: 1,
  // Reconciliación de la lectura: ESTRICTO para BUSCAR, y se entrega igual. Cualquier
  // diferencia en una página la manda a releer, sin tope de relecturas; y el renglón que
  // no aparece ni así entra a la hoja MARCADO en vez de tumbar la cotización
  // (`exigir_cierre` en 0 desde 2026-08-13, migr 083 — ver el comentario del campo).
  // Los tres valores se cambian desde el panel sin desplegar nada.
  reconciliacion_tolerancia_renglones: 0,
  reconciliacion_max_relecturas: 0,
  reconciliacion_exigir_cierre: 0,
  // Medición de modelos IFC (visor de OPUS ci): sus defaults viven en parametrosMedicionIfc.ts.
  ifc_ventana_area: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_ventana_area,
  ifc_ventana_longitud: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_ventana_longitud,
  ifc_ventana_volumen: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_ventana_volumen,
  ifc_cobertura_cara_plana: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_cobertura_cara_plana,
  ifc_ventana_peso: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_ventana_peso,
  ifc_densidad_acero: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_densidad_acero,
  ifc_densidad_concreto: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_densidad_concreto,
  ifc_densidad_madera: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_densidad_madera,
  ifc_densidad_aluminio: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_densidad_aluminio,
  ifc_densidad_vidrio: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_densidad_vidrio,
  ifc_densidad_mamposteria: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_densidad_mamposteria,
  ifc_densidad_yeso: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_densidad_yeso,
  ifc_densidad_concreto_ligero: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_densidad_concreto_ligero,
  ifc_forma_lineal_min: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_forma_lineal_min,
  ifc_aviso_tamano_mb: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_aviso_tamano_mb,
  ifc_forma_plana_min: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_forma_plana_min,
  ifc_nivel_tolerancia: PARAMETROS_MEDICION_IFC_DEFAULT.ifc_nivel_tolerancia,
  // Medición de planos DWG/DXF (lector plano2svg.py): sus defaults viven en parametrosMedicionDwg.ts.
  ...PARAMETROS_MEDICION_DWG_DEFAULT,
  // «¿Este plano sirve para presupuestar?» (OPUS ci): sus defaults viven en chequeoDelPlano.ts.
  ...PARAMETROS_CHEQUEO_PLANO_DEFAULT,
  // «Cambió el plano» (comparar dos versiones de un plano): sus defaults viven en cambioDePlano.ts.
  ...PARAMETROS_CAMBIO_DE_PLANO_DEFAULT,
};

/**
 * Lo que de verdad aplica el motor: lo guardado en la calibración vigente, completado con los defaults.
 * Una clave ausente o no numérica toma su default; las claves desconocidas (de versiones viejas, p. ej.
 * `apu_confianza_alta_min`) se ignoran. Es la MISMA combinación en el worker, la API y el panel.
 */
export function umbralesEfectivos(guardados: unknown): UmbralesCalibracion {
  const src = (guardados && typeof guardados === 'object' ? guardados : {}) as Record<string, unknown>;
  const out = { ...UMBRALES_CALIBRACION_DEFAULT } as UmbralesCalibracion;
  for (const k of Object.keys(out) as (keyof UmbralesCalibracion)[]) {
    const v = src[k];
    if (typeof v === 'number' && Number.isFinite(v)) (out as unknown as Record<string, number>)[k] = v;
  }
  return out;
}
